/**
 * InitiativeEpicsPanel - Reusable panel for Initiative Epics management
 * 
 * Used in:
 * - InitiativeDetailModal (edit mode)
 * - InitiativeMergeModal (source view + merged form)
 * 
 * Props:
 * - linkedEpics: array of epic objects linked to this initiative
 * - isEditing: boolean (controls whether epics can be linked/unlinked)
 * - isLoading: boolean
 * - colors: theme colors from useSafeMode
 * - onAddEpic: () => void (opens add epic modal)
 * - onOpenEpic: (epic) => void (opens epic detail modal)
 * - onUnlinkEpic: (epicId, epicTitle) => void
 * - mode: 'edit' | 'create' | 'view'
 * - canAddEpics: boolean (true if initiative exists and can add epics)
 */

import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { Rocket, Plus, X, ExternalLink, Loader2, BarChart3 } from 'lucide-react';

// Epic status badge colors
const EPIC_STATUS_COLORS = {
  'Backlog': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  'In Progress': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'Done': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const InitiativeEpicsPanel = ({
  linkedEpics = [],
  isEditing = true,
  isLoading = false,
  colors,
  onAddEpic,
  onOpenEpic,
  onUnlinkEpic,
  mode = 'edit',
  canAddEpics = true,
}) => {
  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
          <Rocket className="w-4 h-4" />
          Linked Epics ({linkedEpics.length})
        </Label>
        {isEditing && canAddEpics && mode === 'edit' && onAddEpic && (
          <Button
            size="sm"
            variant="outline"
            className={cn("gap-1", colors.borderPrimary)}
            onClick={onAddEpic}
            data-testid="add-epic-btn"
          >
            <Plus className="w-3 h-3" />
            Add Epic
          </Button>
        )}
      </div>
      
      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
        </div>
      ) : linkedEpics.length === 0 ? (
        /* Empty State */
        <div className={cn("text-center py-8 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <Rocket className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
          <p className={cn("text-sm", colors.textMuted)}>No epics linked to this initiative</p>
          {mode === 'create' && (
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              Save the initiative first to link epics
            </p>
          )}
          {isEditing && canAddEpics && mode === 'edit' && onAddEpic && (
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={onAddEpic}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add First Epic
            </Button>
          )}
        </div>
      ) : (
        /* Epics List */
        <div className="space-y-2">
          {linkedEpics.map(epic => {
            const statusColor = EPIC_STATUS_COLORS[epic.status] || EPIC_STATUS_COLORS['Backlog'];
            const featureProgress = epic.feature_stats 
              ? Math.round((epic.feature_stats.done / epic.feature_stats.total) * 100) || 0
              : 0;
            
            return (
              <div 
                key={epic.epic_id}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
                  colors.bgTertiary, colors.borderPrimary
                )}
                data-testid={`linked-epic-${epic.epic_id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", "bg-purple-100 dark:bg-purple-900/30")}>
                    <Rocket className="w-4 h-4 text-purple-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn("font-medium truncate", colors.textPrimary)}>
                      {epic.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={cn("text-xs px-2 py-0.5 rounded-full", statusColor)}>
                        {epic.status}
                      </span>
                      {epic.feature_stats && (
                        <span className={cn("text-xs flex items-center gap-1", colors.textMuted)}>
                          <BarChart3 className="w-3 h-3" />
                          {epic.feature_stats.done}/{epic.feature_stats.total} features ({featureProgress}%)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Open Epic Button */}
                  {onOpenEpic && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onOpenEpic(epic)}
                      className={cn("text-gray-500 hover:text-gray-700", colors.textMuted)}
                      title="Open epic"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                  
                  {/* Unlink Epic Button */}
                  {isEditing && mode === 'edit' && onUnlinkEpic && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onUnlinkEpic(epic.epic_id, epic.title)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Unlink epic"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Progress Summary - Only show when there are epics */}
      {linkedEpics.length > 0 && (
        <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <div className="flex items-center justify-between text-sm">
            <span className={colors.textSecondary}>Epic Completion</span>
            <span className={cn("font-medium", colors.textPrimary)}>
              {linkedEpics.filter(e => e.status === 'Done').length} / {linkedEpics.length} complete
            </span>
          </div>
          <div className={cn("w-full h-2 rounded-full overflow-hidden mt-2", "bg-gray-200 dark:bg-gray-700")}>
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all"
              style={{ width: `${(linkedEpics.filter(e => e.status === 'Done').length / linkedEpics.length) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InitiativeEpicsPanel;
